import {
  useResizeObserver,
} from '@mantine/hooks';
import {
  createFileRoute,
} from '@tanstack/react-router';
import axios, {
  type AxiosResponse,
} from 'axios';
import type {
  PDFDocumentProxy,
} from 'pdfjs-dist';
import PdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useAuth,
} from 'react-oidc-context';
import {
  Document,
  Page,
  pdfjs,
} from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import '#components/ui/pdf/PdfView.scss';
import {
  ErrorBoundary,
} from '#components/errors/ErrorBoundary';

import type {
  QueryFunctionContext,
  QueryKey,
} from '@tanstack/react-query';

interface PdfViewProps {
  filePath?: string;
}

pdfjs.GlobalWorkerOptions.workerSrc = PdfWorkerUrl;

const options = {
  cMapUrl: 'pdfjs-dist/cmaps/',
  standardFontDataUrl: 'pdfjs-dist/standard_fonts/',
};

const maxWidth = 1200;

export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/omni/_omniLayout/$clientId/$fileName',
)({
  loader: async (opts) => {
    async function pdfQueryFn<
      T = AxiosResponse<any, any>,
      TQueryKey extends QueryKey = QueryKey,
      TPageParam = number,
    >(
      context: QueryFunctionContext<TQueryKey, TPageParam>,
    ): Promise<T> {
      const {
        queryKey,
        meta,
        pageParam,
      } = context;
      const queryKeyStrParts = queryKey.filter(part => (typeof part === 'string'));
      const apiPath = queryKeyStrParts.join('/');
      const url = new URL(`${window.origin}/api/${apiPath}`);
      const res = await axios.get(
        url.toString(),
        {
          headers: {
            Authorization: `Bearer ${opts.context.auth.user?.access_token}`,
          },
          responseType: 'arraybuffer',
        },
      );
      return res.data;
    }
    return {
      pdfResponse: await opts.context.queryClient.ensureQueryData({
        queryKey: [
          'client-communication',
          opts.params.clientId,
          opts.params.fileName,
        ],
        queryFn: pdfQueryFn,
      }),
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const {
    pdfResponse,
  } = Route.useLoaderData();
  const {
    fileName,
    clientId,
  } = Route.useParams();
  const fileUrl = new URL(`${window.origin}/api/client-communication/${clientId}/${fileName}`);
  const fileLocation = {
    url: fileUrl.toString(),
  };
  const [
    numPages,
    setNumPages,
  ] = useState<number>();
  const [
    containerWidth,
    setContainerWidth,
  ] = useState<number>();
  const [
    containerRef,
    contentRect,
  ] = useResizeObserver();
  useMemo(() => {
    setContainerWidth(contentRect.width);
  }, [contentRect]);
  const [
    file,
    setFile,
  ] = useState<File | { url: string }>(fileLocation);
  function onFileChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    const {
      files,
    } = event.target;
    const nextFile = files?.[0];
    if (nextFile) {
      setFile(nextFile);
    }
  }

  function onDocumentLoadSuccess({
    numPages: nextNumPages,
  }: PDFDocumentProxy): void {
    setNumPages(nextNumPages);
  }

  return (
    <>
      <ErrorBoundary
        fallback={ (
          <div
            className="PdfView_container_load"
          >
            <input
              onChange={ onFileChange }
              type="file"
              defaultValue={ fileUrl?.toString() }
            />
          </div>
        ) }
        onReset={ reason => console.info(`Error boundary reset`, reason) }
      >
        <div
          className="PdfView"
        >
          <div
            className="PdfView_container"
          >
            <div
              className="PdfView_container_document"
              ref={ containerRef }
            >
              <Document
                // file={file}
                file={ pdfResponse as File }
                onLoadSuccess={ onDocumentLoadSuccess }
                options={ options }
              >
                { Array.from(
                  new Array(numPages),
                  (_el, index) => (
                    <Page
                      key={ `page_${index + 1}` }
                      pageNumber={ index + 1 }
                      width={ containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth }
                    />
                  ),
                ) }
              </Document>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </>
  );
}
